import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Card,
  CardContent
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Image as ImageIcon,
  DeleteForever as DeleteForeverIcon
} from '@mui/icons-material';
import { deviceTypesAPI } from '../api/deviceTypes';
import { characteristicsAPI } from '../api/characteristics';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function DeviceTypes() {
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [characteristics, setCharacteristics] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCharDialog, setOpenCharDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedDeviceType, setSelectedDeviceType] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [charFormData, setCharFormData] = useState({
    characteristic_id: '',
    is_required: false,
    display_order: 0
  });
  const [editingCharacteristic, setEditingCharacteristic] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [types, chars] = await Promise.all([
        deviceTypesAPI.getAll(),
        characteristicsAPI.getAll()
      ]);
      setDeviceTypes(types);
      setCharacteristics(chars);
    } catch (err) {
      setError('Ошибка загрузки данных: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        description: item.description || ''
      });
      setTabValue(0);
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        description: ''
      });
      setTabValue(0);
    }
    setImageFile(null);
    setOpenDialog(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      let savedType;
      if (editingItem) {
        savedType = await deviceTypesAPI.update(editingItem.id, formData);
      } else {
        savedType = await deviceTypesAPI.create(formData);
      }

      // Upload image if selected
      if (imageFile) {
        await deviceTypesAPI.uploadImage(savedType.id, imageFile);
      }

      await loadData();
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот тип устройства?')) {
      return;
    }

    try {
      await deviceTypesAPI.delete(id);
      await loadData();
    } catch (err) {
      setError('Ошибка удаления: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteImage = async (id) => {
    if (!window.confirm('Удалить изображение?')) {
      return;
    }

    try {
      await deviceTypesAPI.deleteImage(id);
      await loadData();
    } catch (err) {
      setError('Ошибка удаления изображения: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleOpenCharDialog = (deviceType, existingChar = null) => {
    setSelectedDeviceType(deviceType);
    if (existingChar) {
      // Editing existing characteristic
      setEditingCharacteristic(existingChar);
      setCharFormData({
        characteristic_id: existingChar.characteristic_id,
        is_required: existingChar.is_required || false,
        display_order: existingChar.display_order || 0
      });
    } else {
      // Adding new characteristic
      setEditingCharacteristic(null);
      setCharFormData({
        characteristic_id: characteristics.length > 0 ? characteristics[0].id : '',
        is_required: false,
        display_order: deviceType.characteristics ? deviceType.characteristics.length : 0
      });
    }
    setOpenCharDialog(true);
    setError('');
  };

  const handleCloseCharDialog = () => {
    setOpenCharDialog(false);
    setSelectedDeviceType(null);
    setEditingCharacteristic(null);
  };

  const handleCharChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setCharFormData({
      ...charFormData,
      [e.target.name]: value
    });
  };

  const handleAddCharacteristic = async () => {
    setLoading(true);
    setError('');

    try {
      const payload = {
        characteristic_id: parseInt(charFormData.characteristic_id),
        is_required: charFormData.is_required,
        display_order: parseInt(charFormData.display_order)
      };

      if (editingCharacteristic) {
        // Update existing characteristic
        await deviceTypesAPI.updateCharacteristic(
          selectedDeviceType.id,
          editingCharacteristic.characteristic_id,
          payload
        );
      } else {
        // Add new characteristic
        await deviceTypesAPI.addCharacteristic(selectedDeviceType.id, payload);
      }

      await loadData();
      handleCloseCharDialog();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCharacteristic = async (deviceTypeId, characteristicId) => {
    if (!window.confirm('Удалить характеристику из типа устройства?')) {
      return;
    }

    try {
      await deviceTypesAPI.removeCharacteristic(deviceTypeId, characteristicId);
      await loadData();
    } catch (err) {
      setError('Ошибка удаления: ' + (err.response?.data?.detail || err.message));
    }
  };

  const getCharacteristicName = (charId) => {
    const char = characteristics.find(c => c.id === charId);
    return char ? char.name : '-';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Типы устройств</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Добавить тип устройства
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Изображение</TableCell>
              <TableCell>Название</TableCell>
              <TableCell>Описание</TableCell>
              <TableCell>Характеристики</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deviceTypes.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  {item.image_url ? (
                    <Avatar src={item.image_url} variant="rounded" />
                  ) : (
                    <Avatar variant="rounded">
                      <ImageIcon />
                    </Avatar>
                  )}
                </TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.description || '-'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {item.characteristics && item.characteristics.map((dtc) => (
                      <Chip
                        key={dtc.characteristic_id}
                        label={getCharacteristicName(dtc.characteristic_id)}
                        size="small"
                        color={dtc.is_required ? 'error' : 'default'}
                        variant="outlined"
                      />
                    ))}
                    <Chip
                      label="+ Добавить"
                      size="small"
                      variant="outlined"
                      onClick={() => handleOpenCharDialog(item)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(item)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  {item.image_url && (
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteImage(item.id)}
                      color="warning"
                    >
                      <DeleteForeverIcon />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(item.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {deviceTypes.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Нет типов устройств
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Main Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ? 'Редактировать тип устройства' : 'Добавить тип устройства'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="Основное" />
            {editingItem && <Tab label="Характеристики" />}
            <Tab label="Изображение" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <TextField
              fullWidth
              label="Название"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
              helperText="Например: Сервер, Коммутатор, Маршрутизатор"
            />

            <TextField
              fullWidth
              label="Описание"
              name="description"
              value={formData.description}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={3}
            />
          </TabPanel>

          {editingItem && (
            <TabPanel value={tabValue} index={1}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Привязанные характеристики
                  </Typography>
                  <List>
                    {editingItem.characteristics && editingItem.characteristics.map((dtc) => (
                      <ListItem key={dtc.characteristic_id}>
                        <ListItemText
                          primary={getCharacteristicName(dtc.characteristic_id)}
                          secondary={
                            <Box>
                              {dtc.is_required && <Chip label="Обязательное" size="small" color="error" sx={{ mr: 1 }} />}
                              <Typography variant="caption">Порядок: {dtc.display_order}</Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            onClick={() => handleOpenCharDialog(editingItem, dtc)}
                            color="primary"
                            title="Редактировать"
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveCharacteristic(editingItem.id, dtc.characteristic_id)}
                            color="error"
                            title="Удалить"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenCharDialog(editingItem)}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Добавить характеристику
                  </Button>
                </CardContent>
              </Card>
            </TabPanel>
          )}

          <TabPanel value={tabValue} index={editingItem ? 2 : 1}>
            {editingItem && editingItem.image_url && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Текущее изображение:
                </Typography>
                <img src={editingItem.image_url} alt={editingItem.name} style={{ maxWidth: '200px', maxHeight: '200px' }} />
              </Box>
            )}
            <TextField
              fullWidth
              type="file"
              onChange={handleImageChange}
              margin="normal"
              helperText="Поддерживаемые форматы: JPG, PNG, SVG, WebP"
              InputLabelProps={{ shrink: true }}
              inputProps={{ accept: 'image/jpeg,image/png,image/svg+xml,image/webp' }}
            />
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.name}
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Characteristic Dialog */}
      <Dialog open={openCharDialog} onClose={handleCloseCharDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCharacteristic ? 'Редактировать характеристику' : 'Добавить характеристику'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            select
            label="Характеристика"
            name="characteristic_id"
            value={charFormData.characteristic_id}
            onChange={handleCharChange}
            margin="normal"
            required
            disabled={!!editingCharacteristic}
          >
            {characteristics.map((char) => (
              <MenuItem key={char.id} value={char.id}>
                {char.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            type="number"
            label="Порядок отображения"
            name="display_order"
            value={charFormData.display_order}
            onChange={handleCharChange}
            margin="normal"
          />

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={charFormData.is_required}
                  onChange={handleCharChange}
                  name="is_required"
                />
              }
              label="Обязательное поле"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCharDialog}>Отмена</Button>
          <Button
            onClick={handleAddCharacteristic}
            variant="contained"
            disabled={loading || !charFormData.characteristic_id}
          >
            {loading
              ? (editingCharacteristic ? 'Сохранение...' : 'Добавление...')
              : (editingCharacteristic ? 'Сохранить' : 'Добавить')
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DeviceTypes;
